import React, { useState } from 'react';
import BarcodeScanner from '../common/BarcodeScanner';
import { productsAPI, inventoryAPI } from '../../services/api';
import toast from 'react-hot-toast';

export default function BarcodeLookup() {
  const [showScanner, setShowScanner] = useState(false);
  const [product, setProduct] = useState(null);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [manualCode, setManualCode] = useState('');
  const [scanHistory, setScanHistory] = useState([]);

  const handleScan = async (barcode) => {
    setShowScanner(false);
    setLoading(true);
    setProduct(null);
    setBatches([]);

    try {
      // Try barcode lookup first
      let productData = null;
      try {
        const res = await productsAPI.lookupBarcode(barcode);
        productData = res.data;
      } catch {
        // Try search fallback
        const searchRes = await productsAPI.list({ search: barcode, limit: 1 });
        if (searchRes.data.products.length > 0) {
          const fullRes = await productsAPI.get(searchRes.data.products[0].id);
          productData = fullRes.data;
        }
      }

      if (!productData) {
        toast.error(`No product found for: ${barcode}`);
        setLoading(false);
        return;
      }

      // Fetch full product details
      const fullProduct = await productsAPI.get(productData.id || productData.product_id);
      setProduct(fullProduct.data);

      // Fetch batches
      try {
        const batchRes = await inventoryAPI.batches(fullProduct.data.id);
        setBatches(batchRes.data);
      } catch {}

      // Add to scan history
      setScanHistory(prev => [{
        barcode,
        product_name: fullProduct.data.name,
        sku: fullProduct.data.sku,
        timestamp: new Date().toLocaleTimeString('en-IN'),
      }, ...prev.slice(0, 19)]);

      toast.success(`Found: ${fullProduct.data.name}`);
    } catch {
      toast.error('Lookup failed');
    } finally { setLoading(false); }
  };

  const handleManualSearch = (e) => {
    e.preventDefault();
    if (manualCode.trim()) {
      handleScan(manualCode.trim());
      setManualCode('');
    }
  };

  const fmt = (n) => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const scheduleColor = { H: 'badge-red', H1: 'badge-orange', X: 'badge-purple', OTC: 'badge-green' };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Barcode Lookup</h1>
          <p className="subtitle">Scan or enter barcode/SKU to check product details and stock</p>
        </div>
      </div>

      {/* Scan Controls */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="flex gap-3 items-center" style={{ flexWrap: 'wrap' }}>
            <button onClick={() => setShowScanner(true)} className="btn btn-primary btn-lg">
              📷 Open Scanner
            </button>
            <span className="text-secondary">or</span>
            <form onSubmit={handleManualSearch} className="flex gap-2 items-center" style={{ flex: 1 }}>
              <input
                className="form-input"
                placeholder="Enter barcode, SKU, or product name..."
                value={manualCode}
                onChange={e => setManualCode(e.target.value)}
                style={{ flex: 1, minWidth: 200 }}
              />
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? 'Searching...' : 'Search'}
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Scanner Modal */}
      {showScanner && (
        <BarcodeScanner
          onScan={handleScan}
          onClose={() => setShowScanner(false)}
          title="Scan Product Barcode"
          continuous={true}
        />
      )}

      {/* Product Details */}
      {loading && <div className="loading-container"><div className="spinner" /></div>}

      {product && !loading && (
        <div className="grid-2">
          {/* Product Info Card */}
          <div className="card">
            <div className="card-header">
              <h2>Product Details</h2>
              {product.drug_schedule && product.drug_schedule !== 'none' && (
                <span className={`badge ${scheduleColor[product.drug_schedule] || 'badge-gray'}`}>
                  Schedule {product.drug_schedule}
                </span>
              )}
            </div>
            <div className="card-body">
              <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>{product.name}</div>
              {product.generic_name && <div className="text-secondary mb-2">{product.generic_name}</div>}
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 20px', marginTop: 16 }}>
                <div><span className="text-xs text-secondary">SKU</span><div className="font-mono font-semibold">{product.sku}</div></div>
                <div><span className="text-xs text-secondary">HSN Code</span><div className="font-mono">{product.hsn_code}</div></div>
                <div><span className="text-xs text-secondary">Barcode</span><div className="font-mono">{product.barcode || '—'}</div></div>
                <div><span className="text-xs text-secondary">Pack</span><div>{product.unit_of_measure} {product.pack_size ? `(${product.pack_size})` : ''}</div></div>
                <div><span className="text-xs text-secondary">MRP</span><div className="font-bold" style={{ fontSize: 16 }}>₹{fmt(product.mrp)}</div></div>
                <div><span className="text-xs text-secondary">Selling Price</span><div className="font-bold" style={{ fontSize: 16, color: 'var(--primary)' }}>₹{fmt(product.selling_price)}</div></div>
                <div><span className="text-xs text-secondary">Purchase Price</span><div>₹{fmt(product.purchase_price)}</div></div>
                <div><span className="text-xs text-secondary">GST Rate</span><div className="font-semibold">{product.gst_rate}%</div></div>
                <div><span className="text-xs text-secondary">Manufacturer</span><div>{product.manufacturer_name || '—'}</div></div>
                <div><span className="text-xs text-secondary">Category</span><div>{product.category_name || '—'}</div></div>
              </div>

              {product.requires_prescription && (
                <div className="badge badge-red" style={{ marginTop: 12 }}>Requires Prescription</div>
              )}
              {product.is_refrigerated && (
                <div className="badge badge-blue" style={{ marginTop: 8 }}>Refrigerated Storage</div>
              )}
              {product.storage_conditions && (
                <div className="text-sm text-secondary" style={{ marginTop: 8 }}>{product.storage_conditions}</div>
              )}
            </div>
          </div>

          {/* Stock & Batches Card */}
          <div className="card">
            <div className="card-header">
              <h2>Stock & Batches</h2>
              {product.stock && (
                <span className={`badge ${product.stock.reduce((s, st) => s + Number(st.total_quantity), 0) <= product.reorder_level ? 'badge-red' : 'badge-green'}`}>
                  Total: {product.stock.reduce((s, st) => s + Number(st.total_quantity), 0)} units
                </span>
              )}
            </div>

            {/* Stock by Warehouse */}
            {product.stock && product.stock.length > 0 && (
              <div style={{ padding: '12px 20px', borderBottom: '1px solid var(--border-light)' }}>
                <div className="text-xs text-secondary font-semibold mb-2" style={{ textTransform: 'uppercase' }}>Stock by Warehouse</div>
                {product.stock.map((s, i) => (
                  <div key={i} className="flex justify-between items-center" style={{ padding: '4px 0' }}>
                    <span>{s.warehouse_name}</span>
                    <span className="font-mono font-bold">{s.total_quantity} ({s.available_quantity} avail.)</span>
                  </div>
                ))}
              </div>
            )}

            {/* Batches */}
            <div className="table-container">
              <table>
                <thead>
                  <tr><th>Batch</th><th>Warehouse</th><th>Expiry</th><th style={{textAlign:'right'}}>Qty</th><th style={{textAlign:'right'}}>Price</th><th>Status</th></tr>
                </thead>
                <tbody>
                  {batches.map(b => {
                    const daysLeft = b.days_to_expiry;
                    return (
                      <tr key={b.id}>
                        <td className="td-mono font-semibold">{b.batch_number}</td>
                        <td className="text-secondary">{b.warehouse_name}</td>
                        <td style={{ color: daysLeft <= 30 ? 'var(--danger)' : daysLeft <= 90 ? 'var(--accent)' : 'inherit' }}>
                          {new Date(b.expiry_date).toLocaleDateString('en-IN', { month: 'short', year: '2-digit' })}
                          <div className="text-xs">{daysLeft}d</div>
                        </td>
                        <td className="td-amount font-bold">{b.quantity}</td>
                        <td className="td-amount">₹{fmt(b.selling_price)}</td>
                        <td>
                          {daysLeft <= 0 ? <span className="badge badge-red">Expired</span>
                            : daysLeft <= 30 ? <span className="badge badge-red">Critical</span>
                            : daysLeft <= 90 ? <span className="badge badge-orange">Near Expiry</span>
                            : <span className="badge badge-green">OK</span>}
                        </td>
                      </tr>
                    );
                  })}
                  {batches.length === 0 && (
                    <tr><td colSpan={6} className="text-center text-secondary" style={{ padding: 24 }}>No active batches</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Empty state */}
      {!product && !loading && (
        <div className="card">
          <div className="empty-state" style={{ padding: 60 }}>
            <div style={{ fontSize: 48, marginBottom: 12, opacity: 0.3 }}>📷</div>
            <h3>Scan a barcode to get started</h3>
            <p>Use the camera scanner or type a barcode/SKU above to instantly look up product details, pricing, batch information, and current stock levels.</p>
          </div>
        </div>
      )}

      {/* Scan History */}
      {scanHistory.length > 0 && (
        <div className="card" style={{ marginTop: 16 }}>
          <div className="card-header">
            <h2>Scan History (this session)</h2>
            <button onClick={() => setScanHistory([])} className="btn btn-outline btn-sm">Clear</button>
          </div>
          <div className="table-container">
            <table>
              <thead><tr><th>Time</th><th>Barcode/SKU</th><th>Product</th><th>SKU</th></tr></thead>
              <tbody>
                {scanHistory.map((h, i) => (
                  <tr key={i} style={{ cursor: 'pointer' }} onClick={() => handleScan(h.barcode)}>
                    <td className="text-secondary">{h.timestamp}</td>
                    <td className="td-mono">{h.barcode}</td>
                    <td style={{ fontWeight: 600 }}>{h.product_name}</td>
                    <td className="td-mono">{h.sku}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
